import { SmartCollectionInput } from 'api/product/getSmartColleciton/types';
import { useConfig } from 'config';
import { FeatureFlag, getFeatureFlag } from 'constants/feature-flag';
import {
  SegmentationIds,
  getSegmentationFromUrl,
} from 'constants/segmentation';
import { DEFAULT_DYNAMIC_CDP_NAME } from 'constants/test/dynamic-ux-test';
import { PetContextValues } from 'contexts/pet';
import { PetProfile } from 'contexts/pet-profile';
import { SegmentationStore } from 'contexts/segmentation';
import useDynamicTest from 'hooks/test/use-dynamic-ux';
import { DynamicTestScenario } from 'hooks/test/use-dynamic-ux/const';
import { useMemo } from 'react';
import {
  generateInputFromCdpPath,
  generateInputFromPetProfile,
  generateInputFromSegmentationIds,
  mergeDynamicInput,
} from 'utils/api/smartCollection';
import { checkSegmentationMatch } from 'utils/segmentation/check-segmentation-match';
import { omitNullableHandler } from 'utils/type-helper';

type Input = {
  handle: string;
  applyPetProfile: boolean;
  petProfile: PetProfile;
  segmentation?: SegmentationStore;
  currentPetType: PetContextValues;
};

type Output = {
  providerProps:
    | {
        smartCollectionInput: SmartCollectionInput;
        trackingListName: string;
      }
    | {
        collectionHandle: string;
      }
    | {
        segmentation: SegmentationIds | SegmentationStore;
        trackingListName: string;
      };
};

/**
 * Generate props for collection context based on the test scenario
 */
const useCollectionProps = ({
  handle,
  applyPetProfile,
  petProfile,
  segmentation,
  currentPetType,
}: Input): Output => {
  const { shopId } = useConfig();
  const featureFlg = getFeatureFlag(FeatureFlag.dynamicUxTest, shopId);

  const { testScenario } = useDynamicTest();
  /**
   * Generate props based on the scenario
   */
  const providerProps = useMemo(() => {
    // Wrong usage, it creates a cdp input from anypage
    const cdpInput = generateInputFromCdpPath({
      path: `/collections/${handle}`,
    });

    const isGenericPage = !!cdpInput?.food_types;

    const segment = getSegmentationFromUrl({ url: `/collections/${handle}` });

    if ((!segment && !isGenericPage) || !featureFlg) {
      return { smartCollectionInput: { slug: handle } };
    }

    // Remove last segment if current type doesn't match up with last segment
    const lastSegmentInput =
      segmentation &&
      segmentation.lastSegmentation &&
      checkSegmentationMatch({
        currentPetType,
        segmentation: segmentation.lastSegmentation,
      })
        ? generateInputFromSegmentationIds({
            segmentationIds: [segmentation.lastSegmentation],
          })
        : {};

    // for cat segment we give only last segment for general PNJ behavior
    if (currentPetType === 'cats') {
      return { smartCollectionInput: { slug: handle, ...lastSegmentInput } };
    }

    try {
      switch (testScenario) {
        case DynamicTestScenario.control:
          return {
            smartCollectionInput: {
              slug: handle,
              ...lastSegmentInput,
            },
          };

        case DynamicTestScenario.variantA:
          if (isGenericPage) {
            return {
              smartCollectionInput: {
                slug: handle,
                ...lastSegmentInput,
              },
            };
          }
          if (segment) {
            return {
              smartCollectionInput: {
                ...generateInputFromSegmentationIds({
                  segmentationIds: [segment.id],
                }),
                slug: handle,
              },
            };
          }
          return { collectionHandle: handle };

        case DynamicTestScenario.variantB:
        case DynamicTestScenario.variantC:
          if (!isGenericPage) {
            return {
              smartCollectionInput: { slug: handle, ...lastSegmentInput },
            };
          }

          if (
            // assign pet profile in generic page if user wants to
            // applyPetProfile should be AFTER first check because this state is not reset
            // after page change
            applyPetProfile &&
            Object.values(petProfile).flat().filter(omitNullableHandler)
              .length > 0
          ) {
            const inputFromPet = generateInputFromPetProfile({
              petProfile,
              omitFoodType: true,
            });
            const smartCollectionInput = mergeDynamicInput({
              target: cdpInput,
              source: inputFromPet,
            });
            delete smartCollectionInput.food_types;
            return {
              smartCollectionInput: {
                ...smartCollectionInput,
                slug: handle,
              },
            };
          }

          return {
            smartCollectionInput: { slug: handle, ...lastSegmentInput },
          };

        default:
          return { smartCollectionInput: { slug: handle } };
      }
    } catch (error) {
      // fall back for edge cases as there's no point in crushing web page
      console.warn(error);
      return { smartCollectionInput: { slug: handle } };
    }
  }, [
    applyPetProfile,
    currentPetType,
    handle,
    petProfile,
    segmentation,
    featureFlg,
    testScenario,
  ]);

  return {
    providerProps: providerProps['collectionHandle']
      ? providerProps
      : { ...providerProps, trackingListName: DEFAULT_DYNAMIC_CDP_NAME },
  };
};

export default useCollectionProps;
